import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowDown, FaPlay, FaCode, FaMobile, FaGlobe, FaRocket } from 'react-icons/fa';
import './Hero.css';

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section id="home" className="hero">
      {/* Background Elements */}
      <div className="hero-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        
        {/* Animated Grid */}
        <div className="animated-grid">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="grid-line"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.1 }}
              transition={{ delay: i * 0.1, duration: 2 }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero-badge" variants={itemVariants}>
            <FaRocket className="badge-icon" />
            <span>Transforming Ideas Into Digital Reality</span>
          </motion.div>

          <motion.h1 className="hero-title" variants={itemVariants}>
            Building the Future of
            <span className="text-gradient"> Digital Solutions</span>
          </motion.h1>

          <motion.p className="hero-description" variants={itemVariants}>
            We create cutting-edge web applications, stunning websites, 
            innovative mobile apps, and breakthrough tech platforms that 
            push the boundaries of what's possible in the digital world.
          </motion.p>

          <motion.div className="hero-buttons" variants={itemVariants}>
            <motion.a 
              href="#contact"
              className="btn btn-primary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Start Your Project</span>
              <FaArrowDown className="btn-icon" />
            </motion.a>
            
            <motion.a 
              href="#about"
              className="btn btn-secondary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlay className="btn-icon" />
              <span>Learn About Us</span>
            </motion.a>
          </motion.div>

          <motion.div className="hero-stats" variants={itemVariants}>
            <div className="stat">
              <span className="stat-number">50+</span>
              <span className="stat-label">Projects Delivered</span>
            </div>
            <div className="stat">
              <span className="stat-number">15+</span>
              <span className="stat-label">Happy Clients</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">On-Time Delivery</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div 
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {/* Floating Tech Cards */}
          <motion.div
            className="floating-card card-1"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '0s' }}
          >
            <FaCode />
            <span>Web Apps</span>
          </motion.div>

          <motion.div
            className="floating-card card-2"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '2s' }}
          >
            <FaMobile />
            <span>Mobile Apps</span>
          </motion.div>

          <motion.div
            className="floating-card card-3"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '4s' }}
          >
            <FaGlobe />
            <span>Websites</span>
          </motion.div>

          {/* Central Hub */}
          <motion.div 
            className="central-hub"
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              rotate: { duration: 20, repeat: Infinity, ease: "linear" },
              scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <div className="hub-inner">
              <FaRocket />
            </div>
          </motion.div>

          {/* Connection Lines */}
          <svg className="connection-lines" viewBox="0 0 400 400">
            <motion.path
              d="M200,100 Q200,200 300,300"
              stroke="url(#gradient)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 1 }}
            />
            <motion.path
              d="M200,100 Q200,200 100,300"
              stroke="url(#gradient)"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 1.5 }}
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
          </svg>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <motion.div
          className="scroll-line"
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <span>Scroll to explore</span>
      </motion.div>
    </section>
  );
};

export default Hero;
